// src/components/microphone/MicrophoneList.js
import React,{ useEffect,useState } from 'react';
import { FaFilter, FaExclamationTriangle, FaMicrophoneSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import LoadingSpinner from '../common/LoadingSpinner';
import DirectAudioPlayer from './DirectAudioPlayer';
import MicrophoneCard from './MicrophoneCard';
import MicrophoneDetails from './MicrophoneDetails';
import './MicrophoneList.css';
import { useAuth } from '../../context/AuthContext';

/**
 * MicrophoneList Component
 * 
 * Displays microphones as a list with location filtering and waveform visualization
 */
const MicrophoneList = () => {
  const [microphones, setMicrophones] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLocation, setActiveLocation] = useState('all');
  const [locations, setLocations] = useState([]);
  const [showLocationFilter, setShowLocationFilter] = useState(false);
  const [waveformData, setWaveformData] = useState({});
  const [audioData, setAudioData] = useState({});
  const { apiRequest } = useAuth();
  const API_BASE_URL = '';
  const API_PATH = '/api';

  // Fetch microphones and audio data
  useEffect(() => {
    const fetchMicrophones = async () => {
      try {
        setLoading(true);
        const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/microphones`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch microphones');
        }
        
        // const data = await response.json();
        setMicrophones(data);
        
        // Extract and sort unique locations
        const uniqueLocations = [...new Set(data.map(mic => mic.location || 'Unknown'))].sort();
        setLocations(['all', ...uniqueLocations]);
        
        // Decide if we should show location filter dropdown
        // Show it if there are at least 2 different locations
        setShowLocationFilter(uniqueLocations.length > 1);
        
        setError(null);
      } catch (error) {
        console.error('Error fetching microphones:', error);
        setError('Unable to load microphones. Please try again.');
        toast.error('Failed to load microphones');
      } finally {
        setLoading(false);
      }
    };
    
    const setupAudioStream = () => {
      // For demo, we'll simulate receiving random waveform data
      const intervalId = setInterval(() => {
        microphones.forEach(mic => {
          if (mic.status === 'active') {
            // Create random waveform data for active mics
            const waveform = Array.from({length: 50}, () => (Math.random() - 0.5) * 0.8);
            
            // Set waveform data for this mic
            setWaveformData(prev => ({
              ...prev,
              [mic.id]: waveform
            }));
            
            // For real implementation, this would be PCM audio data
            // Here we're just simulating it
            const audioBuffer = new ArrayBuffer(100);
            setAudioData(prev => ({
              ...prev,
              [mic.id]: audioBuffer
            }));
          }
        });
      }, 500);
      
      return () => clearInterval(intervalId);
    };
    
    fetchMicrophones();
    const cleanup = setupAudioStream();
    
    return cleanup;
  }, []);
  
  // Handle mic details view
  const handleMicDetails = (micId) => {
    setSelectedMicId(micId === selectedMicId ? null : micId);
  };
  
  // Handle location filter change
  const handleLocationChange = (location) => {
    setActiveLocation(location);
  };
  
  // Helper to format location name for display
  const formatLocationName = (location) => {
    if (location === 'all') return 'All Locations';
    if (location === 'Unknown') return 'Unknown Location';
    
    return location
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  
  // Filter microphones by location
  const filteredMicrophones = activeLocation === 'all'
    ? microphones
    : microphones.filter(mic => (mic.location || 'Unknown') === activeLocation);
  
  // Get microphone by ID
  const getSelectedMic = () => {
    return microphones.find(mic => mic.id === selectedMicId) || null;
  };
  
  if (loading) {
    return (
      <div className="mic-list-loading">
        <LoadingSpinner size="large" text="Loading microphones..." />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="mic-list-error">
        <div className="error-icon">
          <FaExclamationTriangle />
        </div>
        <h3>Connection Error</h3>
        <p>{error}</p>
        <button 
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (microphones.length === 0) {
    return (
      <div className="mic-list-empty">
        <div className="empty-icon">
          <FaMicrophoneSlash />
        </div>
        <h3>No Microphones Found</h3>
        <p>No microphones are currently available in your smart home system.</p>
      </div>
    );
  }
  
  const selectedMic = getSelectedMic();
  
  return (
    <div className="microphone-container">
      <div className="microphone-header">
        <h2>Microphone Streams</h2>
        
        {/* Location filter */}
        {showLocationFilter && (
          <div className="location-filter">
            <div className="filter-icon">
              <FaFilter />
            </div>
            <select 
              value={activeLocation}
              onChange={(e) => handleLocationChange(e.target.value)}
              className="location-selector"
            >
              {locations.map(location => (
                <option key={location} value={location}>
                  {formatLocationName(location)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      {/* Microphone list */}
      <div className="microphones-list">
        {filteredMicrophones.map(mic => (
          <div key={mic.id} className="microphone-list-item">
            <MicrophoneCard 
              mic={mic}
              isActive={mic.status === 'active'}
              isSelected={selectedMicId === mic.id}
              onSelect={() => handleMicDetails(mic.id)}
            />
            
            {/* Display audio player for selected microphone */}
            {selectedMicId === mic.id && (
              <div className="selected-microphone-player">
                <DirectAudioPlayer 
                  audioData={audioData[mic.id]}
                  waveformData={waveformData[mic.id]}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      
      {selectedMic && (
        <MicrophoneDetails 
          mic={selectedMic}
          isActive={selectedMic.status === 'active'}
          onClose={() => setSelectedMicId(null)}
          onActivate={() => {
            // Normally, we would activate the microphone here
            // But in this demo, the microphone status is managed by the server
            console.log('Activating microphone:', selectedMic.id);
          }}
        />
      )}
    </div>
  );
};

export default MicrophoneList;